import { usePrevious } from "@/hooks/usePrevious.tsx";
import { useScrollToBottom } from "@/hooks/useScrollToBottom.tsx";
import type { ThreadEventItem } from "@/types/threadEvents.ts";
import { type CSSProperties, useEffect, useLayoutEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { useActiveChatScroll } from "./useActiveChatScroll.tsx";

/**
 * Manage the scroll position of the list when new messages are added due to infinite scroll. Generally, we want to avoid
 * the user seeing content jump around unless it's in response to an action they've taken (e.g. sending a message).
 *
 * When new items come in at the top of the list, the browser will want to make the content jump,
 * because by default it keeps a constant offset from the top of the list.
 * We need to counteract this by manually adjusting the scroll position.
 *
 * 1. New messages are added to the top of the list (infinite scroll)
 *    - Visible content should not change -> manually adjust the scroll position.
 * 2. New messages are added to the bottom of the list (user chat/response)
 *    - Visible content should not change -> do nothing, the scroll position is already correct.
 *    - Other code will give the user a visual cue that the new message has been added.
 */
const useInfiniteScrollListPosition = ({
  items,
  scrollContainerRef,
}: {
  items: unknown[];
  scrollContainerRef: React.RefObject<HTMLElement | null>;
}) => {
  const prevItems = usePrevious(items);
  const prevScrollContainerHeight = useRef<number | null>(null);

  // Supporting effect to track the scroll container height. By the time the useLayoutEffect below runs,
  // the scroll container height is updated, so to compare against the previous height, we store it here.
  useEffect(() => {
    if (scrollContainerRef.current) {
      prevScrollContainerHeight.current = scrollContainerRef.current.scrollHeight;
    }
  });

  // **Handle the scroll position when we're not at the bottom of the list**
  // This should ensure that there is no visible content change in this case.
  // TODO: ideally we'd use a resize observer here as well, so if an item further up the list
  // changes height, we can handle that. We'd just need to pick a point to remain fixed, and figure
  // out if the change happened above or below it, then act accordingly.
  useLayoutEffect(() => {
    // Can't do anything if the scroll container ref is not available
    if (!scrollContainerRef.current) return;

    // Don't run when initially mounting the list, that has different autoscroll behavior
    if (!prevItems?.length) return;

    // If the start of the list didn't change, do nothing
    // We don't need to do anything if new items are added to the end of the list:
    // the scroll distance is relative to the top, so by default the visible content will not change.
    if (prevItems?.[0] === items?.[0]) {
      return;
    }

    const oldScrollHeight = prevScrollContainerHeight.current ?? 0;

    // This will run immediately after the DOM is updated with the new items. So the scrollContainer ref can give us the height of
    // the scroll container with the new items in place.
    const scrollContainer = scrollContainerRef.current;
    const currClientHeight = scrollContainer.clientHeight;
    const currScrollHeight = scrollContainer.scrollHeight;

    // Read scroll position here, not when the request for data fires or a render runs, so we have the most up to date value.
    // Otherwise, if a user scrolls after the request is fired/render cycle/etc, it would be outdated.
    const currScrollTop = scrollContainer.scrollTop;

    // NOTE: without `overflow-anchor: "none"`, this scrollContainer.scrollTop behaves inconsistently.
    // - If you are scrolled all the way to the top when this hook runs, scrollTop === 0. (Perhaps it's jumping to the parent scroll container?)
    // - If you are even 1px lower, scrollTop is basically newScrollTop as currently calculated, it's the value that we are manually moving to in this hook.
    //
    // `overflow-anchor` is supposed to do the scroll jumping for us without JS, but
    // A. it's not supported in all browsers
    // B. even with it enabled we need to scroll in JS
    // C. it has this inconsistency when it is enabled
    // So we just disable it below, and do the math on the offset ourselves.
    // If we ever need to re-enable it, we can patch this hook by using a scroll event listener in a separate hook to track the scroll position in ref.
    // @see https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-anchor

    const scrollDelta = currScrollHeight - oldScrollHeight;
    // Ensure we don't over-scroll when near the bottom
    const maxScroll = currScrollHeight - currClientHeight;
    const newScrollTop = Math.min(currScrollTop + scrollDelta, maxScroll);

    console.debug("Scroll container values:", {
      currScrollTop,
      newScrollTop,
      oldScrollHeight,
      currScrollHeight,
      scrollDelta,
      maxScroll,
    });

    // Only adjust if there's actually a change in height
    if (newScrollTop !== currScrollTop) {
      scrollContainer.scrollTop = newScrollTop;
    }
  }, [items, prevItems, scrollContainerRef]);

  const style: CSSProperties = {
    // see NOTE above on currScrollTop
    overflowAnchor: "none",
  };

  return { scrollContainerStyles: style };
};

/**
 * Every scroll concern the thread feed has, in one place: where it starts, how
 * it stays put when history loads above, when to ask for more, whether the user
 * is at the bottom, and how much room to leave so an active message can reach
 * the top.
 *
 * These were six separate pieces threaded through the feed component, all
 * talking to the same container ref. Collecting them makes the ref's owner
 * obvious and keeps the component's remaining scroll surface to the refs it has
 * to attach.
 */
export function useFeedScroll({
  threadId,
  items,
  hasPreviousPage,
  isFetchingPreviousPage,
  loadMore,
  activeUserMessageId,
}: {
  /** The thread the feed shows; a change re-arms the settle pin below. */
  threadId: number;
  items: ThreadEventItem[];
  hasPreviousPage: boolean;
  isFetchingPreviousPage: boolean;
  loadMore: () => void;
  /** Id of the feed's active user message; a change scrolls it to the top. */
  activeUserMessageId: number | undefined;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeUserMessageRef = useRef<HTMLDivElement>(null);

  const [bottomRef, isAtBottom] = useInView({
    // TODO: Note - this will trigger list re-renders when the visible state changes.
    // We use this mostly as treat that as a ref, so we might want to use a different intersection observer setup.
    threshold: 0,
    root: scrollContainerRef.current,
    rootMargin: "0px 0px 400px 0px", // top margin, just a little extra buffer for when we consider ourselves at the bottom.
  });

  // Always start at the bottom (most recent message) on initial load. We no
  // longer persist/restore the scroll position across mounts. A layout effect
  // (pre-paint) avoids a flash of content at a different position, and the ref
  // ensures this runs only once for the component's lifetime — later updates
  // (new messages, infinite-scroll-up) are handled by the hooks below.
  const scrollToInitialBottom = useScrollToBottom(scrollContainerRef);
  const hasScrolledToBottomRef = useRef(false);
  useLayoutEffect(() => {
    if (hasScrolledToBottomRef.current) return;
    if (!scrollContainerRef.current || !items.length) return;
    scrollToInitialBottom("instant");
    hasScrolledToBottomRef.current = true;
  }, [items, scrollToInitialBottom]);

  // That one scroll is not enough to land a reopened thread on its latest
  // message: content keeps arriving after it fires (the docked artifact card
  // and its clearance mount when their own query resolves), growing the feed
  // below the fold so the newest message ends up hidden behind the card. Until
  // the user touches the feed or the active-message glide takes over, the
  // bottom stays pinned: any size change snaps back instantly, so late cards
  // and slow server turns land in view instead of below the fold.
  const isPinnedToBottomRef = useRef(true);

  // The list instance survives a switch to another cached thread (wouter
  // re-renders the same panel when only `:threadId` changes), so a pin the
  // user released on the last thread would otherwise carry over and leave the
  // next thread's late card growth below the fold. Re-arm on an actual thread
  // change (the ref comparison, read in the body, is also what keeps the dep
  // from being lint-stripped) — before the active-message unpin below, so a
  // thread arriving with an active message still hands the position straight
  // to the glide.
  const pinnedThreadIdRef = useRef(threadId);
  useLayoutEffect(() => {
    if (pinnedThreadIdRef.current === threadId) return;
    pinnedThreadIdRef.current = threadId;
    isPinnedToBottomRef.current = true;
  }, [threadId]);

  // Any interaction inside the feed (scrolling, or clicking anything — a card
  // action, a link, the composer) means the user has taken over. Keyed on
  // `items` like the initial scroll above: the container ref is empty until
  // the loading state gives way to the real list, so a once-only effect would
  // attach to nothing.
  useEffect(() => {
    if (!items.length) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const unpin = () => {
      isPinnedToBottomRef.current = false;
    };
    const events = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
    for (const event of events) container.addEventListener(event, unpin, { passive: true });
    return () => {
      for (const event of events) container.removeEventListener(event, unpin);
    };
  }, [items]);

  // The active-message glide owns the position from the moment the user's own
  // message (or a card answer's echo) lands, so it must not race the snap. A
  // layout effect so the unpin is in place before the resize observer below
  // sees the growth that the message and its spacer cause in the same commit.
  useLayoutEffect(() => {
    if (activeUserMessageId !== undefined) {
      isPinnedToBottomRef.current = false;
    }
  }, [activeUserMessageId]);

  useEffect(() => {
    if (!items.length) return;
    const container = scrollContainerRef.current;
    const content = container?.firstElementChild;
    if (!container || !content) return;
    const observer = new ResizeObserver(() => {
      if (!isPinnedToBottomRef.current) return;
      scrollToInitialBottom("instant");
    });
    // The content grows as late queries resolve; the container itself resizes
    // with the pane. Both should keep the bottom in view while pinned.
    observer.observe(content);
    observer.observe(container);
    return () => observer.disconnect();
  }, [items, scrollToInitialBottom]);

  const { scrollContainerStyles } = useInfiniteScrollListPosition({ items, scrollContainerRef });

  /**
   * Infinite scroll "load more" trigger.
   *
   * Uses an intersection observer under the hood.
   */
  const [triggerLoadMoreRef, loaderInView] = useInView({
    threshold: 0.1,
    skip: isFetchingPreviousPage || !hasPreviousPage,
    rootMargin: "0px 0px 400px 0px", // the 400px gives us buffer, so it starts loading before it's actually visible
  });

  // infinite scroll when the load more sentinel is in view
  useEffect(() => {
    if (loaderInView) {
      loadMore();
    }
  }, [loaderInView, loadMore]);

  const { spacerHeight } = useActiveChatScroll({
    scrollContainerRef,
    activeUserMessageRef,
    activeUserMessageId,
  });

  return {
    scrollContainerRef,
    activeUserMessageRef,
    bottomRef,
    isAtBottom,
    triggerLoadMoreRef,
    scrollContainerStyles,
    spacerHeight,
  };
}
